import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Menu, X } from 'lucide-react';

const navLinks = (t) => [
  { to: '/', label: t.nav.home, testid: 'nav-link-home', match: '/' },
  { to: '/#brandiagentti', label: t.nav.brandAgent, testid: 'nav-link-brand-agent', match: null },
  { to: '/palvelut', label: t.nav.services, testid: 'nav-link-services', match: '/palvelut' },
  { to: '/meista', label: t.nav.about, testid: 'nav-link-about', match: '/meista' },
  { to: '/ukk', label: t.nav.faq, testid: 'nav-link-faq', match: '/ukk' },
  { to: '/yhteys', label: t.nav.contact, testid: 'nav-link-contact', match: '/yhteys' },
];

const Header = () => {
  const { t, isLanguageSwitchEnabled, toggleLanguage, language } = useLanguage();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={
        'fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-200 ' +
        (scrolled
          ? 'bg-bg/90 backdrop-blur-[16px] border-b border-line'
          : 'bg-bg/70 backdrop-blur-[12px] border-b border-transparent')
      }
    >
      <div className="container-page flex items-center justify-between h-16 md:h-[76px]">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo-link" aria-label="Agencive — Etusivu">
          <picture>
            <source
              media="(max-width: 640px)"
              srcSet="/brand/agencive/transparent/logo-mark-black-transparent.png"
            />
            <img
              src="/brand/agencive/transparent/logo-horizontal-black-transparent.png"
              alt="Agencive"
              width="170"
              height="40"
              className="h-9 md:h-10 w-auto max-w-[95px] sm:max-w-[170px] object-contain"
              style={{ objectFit: 'contain', height: 'auto' }}
              decoding="async"
            />
          </picture>
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5" aria-label="Primary">
          {navLinks(t).map((l) => {
            const active = l.match ? location.pathname === l.match : false;
            return (
              <Link
                key={l.label}
                to={l.to}
                data-testid={l.testid}
                className={
                  'px-3 py-2 rounded-pill text-[14px] transition-colors duration-150 ' +
                  (active ? 'text-ink' : 'text-ink-muted hover:text-ink')
                }
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          {isLanguageSwitchEnabled && (
            <button
              type="button"
              onClick={toggleLanguage}
              data-testid="lang-toggle"
              aria-label="Toggle language"
              className="h-9 px-3 rounded-pill text-[13px] font-medium text-ink-muted hover:text-ink border border-line hover:border-ink/40 transition-colors duration-150"
            >
              {language === 'fi' ? 'EN' : 'FI'}
            </button>
          )}
          <Link
            to="/yhteys#contact-form"
            data-testid="nav-cta-contact"
            className="hidden md:inline-flex items-center h-9 px-4 rounded-pill bg-brand text-white text-[14px] font-medium hover:bg-brand-hover transition-colors duration-150"
          >
            {t.nav.bookCta}
          </Link>
          <button
            type="button"
            className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-pill border border-line text-ink hover:border-ink/40 transition-colors"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Sulje valikko' : 'Avaa valikko'}
            aria-expanded={open}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-line bg-bg" data-testid="mobile-menu-panel">
          <nav className="container-page py-4 flex flex-col" aria-label="Mobile">
            {navLinks(t).map((l) => (
              <Link
                key={l.label}
                to={l.to}
                className="py-3.5 text-ink text-[16px] border-b border-line last:border-0"
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/yhteys#contact-form"
              className="mt-4 inline-flex items-center justify-center h-12 rounded-pill bg-brand text-white text-[15px] font-medium"
            >
              {t.nav.bookCta}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
